import * as React from 'react';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import ConfirmPopups from './ConfirmPopup';
import { CheckPermissions, GetQuery, PostQuery } from './Shared';

interface InvoiceCredit {
    id: number;
    jobId: number;
    siteAddress: string;
    dashInvoiceNumber: number;
    reckonInvoiceNumber: number;
    customerName: string;
    customerAddress: string;
    creditDescription: string;
    creditAmountFormatted: string;
    jobInsertedBy: string;
}

const InvoiceCreditData = () => {
    let navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [invoiceCredits, setInvoiceCredits] = React.useState<InvoiceCredit[]>([]);
    const [invoiceCreditTotal, setInvoiceCreditTotal] = React.useState(0);
    const [showConfirmDelete, setShowConfirmDelete] = React.useState(false);
    const [invoiceCreditId, setInvoiceCreditId] = React.useState(0);    //used when deleting
    const [saveDisabled, setSaveDisabled] = React.useState(false);

    React.useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("ViewSendInvoiceCredits");
        if (hasPermission) {
            getInvoiceCredits();
        } else {
            navigate("/accessdenied");
        }
    }

    const getInvoiceCredits = async () => {
        const apiUrl = "GetInvoiceCredits";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setInvoiceCredits(result.data.credits);
            setInvoiceCreditTotal(result.data.creditTotalFormatted);
            setLoading(false);
        } 
    }

    const complete = (e: any, itemIndex: number) => {
        e.preventDefault();

        if (saveDisabled) {
            return;
        }
        setSaveDisabled(true);

        var creditItem = invoiceCredits[itemIndex];

        completeCredit(creditItem.id);
    }

    const completeCredit = async (creditId: number) => {
        var creditToComplete = {
            creditId: creditId
        };
        var jsonItem = JSON.stringify(creditToComplete);

        const apiUrl = "CompleteInvoiceCredit";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
                setSaveDisabled(false);
            } else {
                setSaveDisabled(false);
                toast.success("Credit has been completed successfully.");
                getInvoiceCredits();
            } 
        }
    }

    const deleteCredit = (e: any, itemIndex: number) => {
        e.preventDefault();

        var creditItem = invoiceCredits[itemIndex];
        setInvoiceCreditId(creditItem.id);
        //check if they want to delete
        setShowConfirmDelete(true);
    }

    const hideConfirmDelete = () => {
        setInvoiceCreditId(0);
        setShowConfirmDelete(false);
    }

    const deleteCreditItem = async () => {
        var creditToDelete = {
            creditId: invoiceCreditId
        };

        var jsonItem = JSON.stringify(creditToDelete);

        const apiUrl = "DeleteInvoiceCredit";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
            setSaveDisabled(false);
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
                setSaveDisabled(false);
                setInvoiceCreditId(0);
                setShowConfirmDelete(false);
            } else {
                setSaveDisabled(false);
                setInvoiceCreditId(0);
                setShowConfirmDelete(false);
                toast.success("Credit has been deleted successfully.");
                getInvoiceCredits();
            }
        }
    }

    const confirmDelete = <ConfirmPopups heading="Delete Credit" text="Are you sure you want to delete this credit?" showConfirmModal={showConfirmDelete} hideConfirmModal={hideConfirmDelete} saveConfirmModal={deleteCreditItem} />

    const renderDetails = (
        <div>
            <div>
                {confirmDelete}
            </div>
            <h2 className="makeitred">An Adjustment Note / Credit needs to be raised for the following jobs</h2>
            <div className="table__margin overflowAuto">
                <table className="table table__large tableColours">
                    <thead>
                        <tr>
                            <th className="hidden"></th>
                            <th>Job #</th>
                            <th>Job Created By</th>
                            <th>Site Address</th>
                            <th>Inv #</th>
                            <th>Reckon Inv #</th>
                            <th>Customer</th>
                            <th>Credit Reason</th>
                            <th>Amount</th>
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    {invoiceCredits.map((credit, index) =>
                        <tbody key={credit.id}>
                            <tr>
                                <td className="hidden">{credit.id}</td>
                                <td className="table__text--align">
                                    <Link to={'/job/' + credit.jobId + '/details'} target="_blank">
                                        {credit.jobId}
                                    </Link>
                                </td>
                                <td className="table__text--align">{credit.jobInsertedBy}</td>
                                <td className="table__text--align">{credit.siteAddress}</td>
                                <td className="table__text--align">{credit.dashInvoiceNumber == 0 ? "" : credit.dashInvoiceNumber}</td>
                                <td className="table__text--align">{credit.reckonInvoiceNumber == 0 ? "" : credit.reckonInvoiceNumber}</td>
                                <td className="table__text--align">{credit.customerName}</td>
                                <td className="table__text--align">{credit.creditDescription}</td>
                                <td className="table__text--align">{credit.creditAmountFormatted}</td>
                                <td className="table__text--align">
                                    <button className="btn btn-success" type="button" onClick={(e) => complete(e, index)} disabled={saveDisabled}>Complete</button>
                                </td>
                                <td className="table__text--align">
                                    <div className="delete--tablecell">
                                        <a className="makeitred" href="#" onClick={(e) => deleteCredit(e, index)}>
                                            <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                        </a>
                                    </div>
                                </td>
                            
                            </tr>
                        </tbody>
                    )}
                </table>
            </div>
            <div className={invoiceCredits.length > 0 ? "colAlignRight" : "hidden"}>
                <div className="invoicing__total--large">Total {invoiceCreditTotal}</div>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default InvoiceCreditData;